import React, { useEffect, useState } from "react";
import {
    Badge, Button, CircularProgress, Grid, Table, TableBody, TableCell,
    TableContainer, TableHead, TableRow, Tooltip, Typography, useTheme
} from "@mui/material";
import { connect } from "react-redux";
//--------------------------|| 3RD PARTY LIBRARIES ||------------------------------------//
import {
    IconGridDots,
    IconFilter,
    IconPlaylistAdd,
    IconSortAscending2,
    IconSortDescending2
} from "@tabler/icons-react";
import { Link } from "react-router-dom";
//--------------------------|| FILES IMPORT ||------------------------------------------//
import { getPager } from "helper/common";
import MainCard from "ui-component/cards/MainCard";
import SubCard from "ui-component/cards/SubCard";
import { NoData } from "ui-component/custom-components/nodata";
import { VWPagination } from "ui-component/custom-components/pagination";
import CreateCustomer from "./create";
import FilterCustomers from "./filter";
import {
    clearCustomerList,
    closeCustomerFilterDialog,
    closeCustomerCreateDialog,
    getCustomerList,
    openCustomerFilterDialog,
    openCustomerCreateDialog,
    setCustomerFilteredData,
    customerListErrorClear
} from "store/app/customer-management/actions";
import { ErrorAlert } from "ui-component/custom-components/alertMessage";
import Chip from "ui-component/extended/Chip";

const tableHeaderStyle = { paddingLeft: '0px' }

const CustomersList = (props) => {
    //--------------------List--------------------------------------
    const { customerList, totalCounts, isPending, errorMessage } = props;

    const [pageSize, setPageSize] = useState(10);
    const [Pager, setPager] = useState({ currentPage: 1 });
    const [sortDirection, setSortDirection] = useState('asc');
    const [sortOrder, setSortOrder] = useState(null);

    //--------------------Filter------------------------------------
    const { isCustomerFilterDialogOpen, customerFilterData } = props;
    //--------------------Create------------------------------------
    const { isCustomerCreateDialogOpen } = props;
    //--------------------Profile-----------------------------------
    const { userProfile } = props;
    const { userRoleName } = userProfile || {};

    //--------------------UI Design---------------------------------
    const theme = useTheme();
    const colorPrimary = theme.palette.secondary.dark;
    const [tableHeight, setTableHeight] = useState(window.innerHeight);

    //---------------------------------------------------------------------------------------//
    useEffect(() => {
        props.customerListErrorClear();

        const handleResize = () => {
            setTableHeight(window.innerHeight)
        };
        window.addEventListener("resize", handleResize);
        return () => {
            props.clearCustomerList();
            window.removeEventListener("resize", handleResize);
        }
    }, [])

    //--------------------------|| SORTING ||------------------------------------------//
    const handleSort = (column) => {
        if (sortOrder === column) {
            setSortDirection(sortDirection === 'asc' ? 'desc' : 'asc');
        }
        else {
            setSortOrder(column);
            setSortDirection('asc');
        }
    };

    //--------------------------|| PAGINATION ||------------------------------------------//
    const setPage = (page = 1) => {
        props.getCustomerList(page, pageSize, sortOrder, sortDirection, customerFilterData);

        setPager(prevPager => {
            const totalPages = Math.ceil(totalCounts / pageSize);
            const visiblePages = 4;
            const pageOffset = Math.max(0, Math.floor((page - 1) / visiblePages) * visiblePages);
            const newPages = [];
            for (let i = pageOffset + 1; i <= Math.min(totalPages, pageOffset + visiblePages); i++) {
                newPages.push(i);
            }
            return {
                ...prevPager,
                currentPage: page,
                totalPages,
                pages: newPages,
            }
        })
    }

    useEffect(() => {
        const currentPager = getPager(totalCounts, 1, pageSize);
        setPager(currentPager)
    }, [totalCounts]);

    useEffect(() => {
        setPage();
    }, [customerFilterData, sortOrder, sortDirection, pageSize])


    //--------------------------|| CREATE DIALOG ||------------------------------------------//
    const handleCustomerCreateOpen = () => {
        props.openCustomerCreateDialog();
    }

    const handleCustomerCreateClose = () => {
        props.closeCustomerCreateDialog();
    }

    //--------------------------|| FILTER DIALOG ||------------------------------------------//
    const handleFilterOpen = () => {
        props.openCustomerFilterDialog();
    }

    const handleFilterClose = () => {
        props.closeCustomerFilterDialog();
    }

    return (
        <>
            {/*----------------------------|| HEADER ||--------------------------------*/}
            <SubCard sx={{
                p: 1.5,
                pl: 2,
                display: 'flex',
                justifyContent: 'space-between',
                alignItems: 'center'
            }}
            >
                <Grid item sx={{ display: 'flex', gap: { lg: 2, md: 2, sm: 1, xs: 1 }, alignItems: "center", marginRight: "10px" }}>
                    <Typography variant="h4">Customers List - </Typography>
                    <Tooltip title="Customer Count" placement="top">
                        <span>
                            <Chip
                                label={totalCounts}
                                chipcolor="secondary"
                                size="small"
                                sx={{
                                    borderRadius: "20%",
                                    py: 1,
                                    fontWeight: "bold",
                                    fontSize: 15,
                                    cursor: "pointer"
                                }}
                            />
                        </span>
                    </Tooltip>
                </Grid>
                <Grid item sx={{ display: 'flex', gap: 2 }}>
                    <Badge
                        badgeContent={customerFilterData ? Object.keys(customerFilterData).length : 0}
                        color="secondary"
                        invisible={customerFilterData ? Object.keys(customerFilterData).length === 0 : true}
                    >
                        <Button
                            variant="contained"
                            size="small"
                            onClick={handleFilterOpen}
                            startIcon={<IconFilter />}
                        >
                            Filter
                        </Button>
                    </Badge>
                    {(userRoleName === "Administrator" || userRoleName === "Master admin") &&
                        <Button
                            variant="contained"
                            size="small"
                            onClick={handleCustomerCreateOpen}
                            startIcon={<IconPlaylistAdd />}
                        >
                            Create Customer
                        </Button>
                    }
                </Grid>
            </SubCard>
            {/*----------------------------|| TABLE ||--------------------------------*/}
            <MainCard sx={{ marginTop: '15px' }}>
                {errorMessage && <ErrorAlert type={'error'} content={errorMessage} />}
                <TableContainer sx={{ whiteSpace: "nowrap", maxHeight: `calc(${tableHeight}px - 380px)`, overflow: "auto" }}>
                    <Table size="small" stickyHeader aria-label="sticky table"
                        sx={{
                            '& tr > *:last-child': {
                                position: 'sticky',
                                right: 0,
                                bgcolor: 'white',
                            },
                        }}
                    >
                        <TableHead>
                            <TableRow>
                                <TableCell>
                                    <Button sx={tableHeaderStyle}
                                        onClick={() => handleSort('CustomerName')}
                                        endIcon={sortOrder === 'CustomerName' && (
                                            sortDirection === 'asc' ?
                                                <IconSortAscending2 color={colorPrimary} />
                                                : <IconSortDescending2 color={colorPrimary} />
                                        )}
                                    >
                                        Customer Name
                                    </Button>
                                </TableCell>
                                <TableCell>
                                    <Button
                                        sx={tableHeaderStyle}
                                        onClick={() => handleSort('Email')}
                                        endIcon={sortOrder === 'Email' && (
                                            sortDirection === 'asc' ?
                                                <IconSortAscending2 color={colorPrimary} />
                                                : <IconSortDescending2 color={colorPrimary} />
                                        )}
                                    >
                                        Email Address
                                    </Button>
                                </TableCell>
                                <TableCell>
                                    <Button
                                        sx={tableHeaderStyle}
                                        onClick={() => handleSort('MobileNumber')}
                                        endIcon={sortOrder === 'MobileNumber' && (
                                            sortDirection === 'asc' ?
                                                <IconSortAscending2 color={colorPrimary} />
                                                : <IconSortDescending2 color={colorPrimary} />
                                        )}
                                    >
                                        Mobile Number
                                    </Button>
                                </TableCell>
                                <TableCell>
                                    <Button
                                        sx={tableHeaderStyle}
                                        onClick={() => handleSort('Address')}
                                        endIcon={sortOrder === 'Address' && (
                                            sortDirection === 'asc' ?
                                                <IconSortAscending2 color={colorPrimary} />
                                                : <IconSortDescending2 color={colorPrimary} />
                                        )}
                                    >
                                        Address
                                    </Button>
                                </TableCell>
                                <TableCell>
                                    <Button
                                        sx={tableHeaderStyle}
                                        onClick={() => handleSort('IsActive')}
                                        endIcon={sortOrder === 'IsActive' && (
                                            sortDirection === 'asc' ?
                                                <IconSortAscending2 color={colorPrimary} />
                                                : <IconSortDescending2 color={colorPrimary} />
                                        )}
                                    >
                                        Customer Status
                                    </Button>
                                </TableCell>
                                <TableCell>View</TableCell>
                            </TableRow>
                        </TableHead>
                        <TableBody>
                            {(customerList?.length > 0) && !isPending ?
                                customerList?.map((data, index) => (
                                    <TableRow key={index}>
                                        <TableCell>{data.CustomerName || 'N/A'}</TableCell>
                                        <TableCell>{data.Email || 'N/A'}</TableCell>
                                        <TableCell>{data.MobileNumber || 'N/A'}</TableCell>
                                        <TableCell>{data.Address || 'N/A'}</TableCell>
                                        <TableCell>
                                            {data.IsActive ?
                                                <Chip label={"Active"} chipcolor="success" size="small" />
                                                : <Chip label={"Inactive"} chipcolor="error" size="small" />
                                            }
                                        </TableCell>
                                        <TableCell>
                                            <Link to={`/customers/details/${data.Id}`}>
                                                <IconGridDots color={theme.palette.primary.main} />
                                            </Link>
                                        </TableCell>
                                    </TableRow>
                                ))
                                /*--------|| LOADING ||---------*/
                                : isPending ?
                                    <TableRow >
                                        <TableCell colSpan={8} height='300px'>
                                            <Grid container alignItems={"center"} justifyContent={"center"} gap={1}
                                                sx={{
                                                    position: "sticky",
                                                    width: "fit-content",
                                                    top: "50%",
                                                    left: "50%",
                                                    transform: "translate(-50%, -50%)"
                                                }}
                                            >
                                                <CircularProgress />
                                                <Typography variant="h5" color={'primary'}> Loading...</Typography>
                                            </Grid>
                                        </TableCell>
                                    </TableRow>
                                    /*--------|| NO DATA ||---------*/
                                    : <TableCell colSpan={8} height='300px'>
                                        <NoData />
                                    </TableCell>
                            }
                        </TableBody>
                    </Table>
                </TableContainer>
                {/*--------|| PAGINATION ||---------*/}
                <Grid>
                    <VWPagination
                        pageDetails={Pager}
                        pageChange={setPage}
                        totalRecords={totalCounts}
                        pageSize={pageSize}
                        setPageSize={setPageSize}
                    />
                </Grid>
            </MainCard>

            {/*--------|| CREATE DIALOG ||---------*/}
            <CreateCustomer
                filter={customerFilterData}
                paginationData={{
                    page: (Pager && Pager.currentPage) || 1,
                    pageSize: pageSize,
                    sortDirection: sortDirection,
                    sortOrder: sortOrder
                }}
                open={isCustomerCreateDialogOpen}
                onClose={handleCustomerCreateClose}
            />

            {/*--------|| FILTER DIALOG ||---------*/}
            <FilterCustomers
                open={isCustomerFilterDialogOpen}
                onClose={handleFilterClose}
                setPage={setPage}
            />
        </>
    )
}

const mapStateToProps = (state) => ({
    //--------------------List--------------------------------------
    customerList: state.customers.customerList,
    totalCounts: state.customers.customerListCount,
    isPending: state.customers.isCustomerListPending,
    errorMessage: state.customers.customerListErrorMessage,
    //--------------------Filter------------------------------------
    isCustomerFilterDialogOpen: state.customers.isCustomerFilterDialogOpen,
    customerFilterData: state.customers.customerFilterData,
    //--------------------Create------------------------------------
    isCustomerCreateDialogOpen: state.customers.isCustomerCreateDialogOpen,
    //--------------------Profile-----------------------------------
    userProfile: state.login.userProfile,
})

const mapDispatchToProps = (dispatch) => ({
    //--------------------List--------------------------------------
    getCustomerList: (page, pageSize, sortOrder, sortDirection, filter) => dispatch(getCustomerList(page, pageSize, sortOrder, sortDirection, filter)),
    clearCustomerList: () => dispatch(clearCustomerList()),
    customerListErrorClear: () => dispatch(customerListErrorClear()),
    //--------------------Filter------------------------------------
    openCustomerFilterDialog: () => dispatch(openCustomerFilterDialog()),
    closeCustomerFilterDialog: () => dispatch(closeCustomerFilterDialog()),
    setCustomerFilteredData: (filter) => dispatch(setCustomerFilteredData(filter)),
    //--------------------Create------------------------------------
    openCustomerCreateDialog: () => dispatch(openCustomerCreateDialog()),
    closeCustomerCreateDialog: () => dispatch(closeCustomerCreateDialog()),
})
export default connect(mapStateToProps, mapDispatchToProps)(CustomersList);